import React, { useState, useEffect, useCallback, useMemo, useImperativeHandle, forwardRef } from 'react';
import { Stack, Typography, Select, MenuItem, FormControl, Box, CircularProgress, SelectChangeEvent } from '@mui/material';
import { Question } from 'models/QuestionModel';
import questionEditorService from 'api/questionEditorService';
import TokenInput from './TokenInput';
import FileUpload from './FileUpload';
import DeleteAttachment from './DeleteAttachment';
import { SavedAnswer } from 'models/SavedAnswerModel';
import { isValidFileType } from 'utils/fileUtilities';
import { DraftAnswerValues, DraftTokenValues } from 'hooks/useOrderItemDraft';
import {
  findIndexOfAnswer,
  getSelectedAnswer,
  isFileUploadRequired,
  shouldShowFileUpload,
  tokensForQuestion
} from 'utils/questionUtilities';

export interface UploadedFile {
  questionId: string;
  file: File;
}

interface QuestionsProps {
  answerValues: DraftAnswerValues;
  tokenValues: DraftTokenValues;
  onAnswerValueChange: (questionId: string, value: string) => void;
  onTokenValueChange: (questionId: string, token: string, value: any) => void;
  onFormValidityChange: (isValid: boolean) => void;
  isReadOnly?: boolean;
  productCode: string;
  onFilesChange?: (files: UploadedFile[]) => void;
  orderItemPlans: URL[];
  onAutoSave?: () => Promise<void> | void;
  onFileDelete?: (fileUrl: URL) => void;
  onFileUpload?: (questionId: string, file: File) => Promise<void>;
  onPlacedOnHoldCompleted?: (comment: string) => Promise<void>;
  onInvalidTokensFound?: (hasInvalidTokens: boolean) => void;
  onLoadError?: (hasError: boolean) => void;
  onQuestionSetVersionChange?: (version: string) => void;
}

export interface QuestionsHandle {
  getFormattedAnswers: () => Record<string, SavedAnswer> | null;
  clearFiles: () => void;
}

const DefaultOption = 'Default Option';
const EmptyConditionPrefix = 'empty-condition-';

const findDefaultOptionIndex = (question: Question): number => question.Answers?.findIndex((answer) => !answer.Condition?.trim()) ?? -1;

const findOnlyMeaningfulAnswerIndex = (question: Question): number => {
  const answers = question.Answers ?? [];

  if (answers.length <= 1 || !answers[0].Condition?.trim()) {
    return -1;
  }

  const meaningfulAnswers = answers.filter((answer) => answer.Condition?.trim() !== '');

  if (meaningfulAnswers.length !== 1) {
    return -1;
  }

  return answers.findIndex((answer) => answer === meaningfulAnswers[0]);
};

const getSelectedAnswerIndex = (question: Question, savedValue?: string): string | undefined => {
  const savedAnswerIndex = savedValue ? findIndexOfAnswer(question, savedValue) : -1;

  if (savedAnswerIndex !== -1) {
    return savedAnswerIndex.toString();
  }

  if (savedValue === DefaultOption) {
    const defaultOptionIndex = findDefaultOptionIndex(question);
    return defaultOptionIndex !== -1 ? defaultOptionIndex.toString() : undefined;
  }

  if (question.Answers?.length === 1) {
    return '0';
  }

  const onlyMeaningfulAnswerIndex = findOnlyMeaningfulAnswerIndex(question);
  return onlyMeaningfulAnswerIndex !== -1 ? onlyMeaningfulAnswerIndex.toString() : undefined;
};

const Questions = forwardRef<QuestionsHandle, QuestionsProps>(
  (
    {
      answerValues,
      tokenValues,
      onAnswerValueChange,
      onTokenValueChange,
      onFormValidityChange,
      isReadOnly,
      productCode,
      onFilesChange,
      orderItemPlans,
      onAutoSave,
      onFileDelete,
      onFileUpload,
      onPlacedOnHoldCompleted,
      onInvalidTokensFound,
      onLoadError,
      onQuestionSetVersionChange
    },
    ref
  ) => {
    const [questions, setQuestions] = useState<Question[]>([]);
    const [placeholders, setPlaceholders] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [files, setFiles] = useState<Record<string, File | null>>({});
    const [companies, setCompanies] = useState<any[]>([]);
    const [uploadingFiles, setUploadingFiles] = useState<Record<string, boolean>>({});

    // Confirmation popup state
    const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);
    const [fileToDelete, setFileToDelete] = useState<{ questionId: string; fileUrl: URL } | null>(null);

    const AllowedExtensions = import.meta.env.VITE_APP_ATTACHMENT_ALLOWED_EXTENSIONS;

    const answers = useMemo((): Record<string, string> => {
      const loadedAnswers: Record<string, string> = {};

      questions.forEach((question) => {
        const selectedAnswerIndex = getSelectedAnswerIndex(question, answerValues[question.Id]);

        if (selectedAnswerIndex !== undefined) {
          loadedAnswers[question.Id] = selectedAnswerIndex;
        }
      });

      return loadedAnswers;
    }, [answerValues, questions]);

    // Function to clear files
    const clearFiles = useCallback(() => {
      setFiles({});
    }, []);

    // Calculate uploaded files as derived state
    const uploadedFiles = useMemo((): UploadedFile[] => {
      const result: UploadedFile[] = [];
      Object.entries(files).forEach(([questionId, file]) => {
        if (file) {
          result.push({ questionId, file });
        }
      });
      return result;
    }, [files]);

    // Notify parent of file changes
    useEffect(() => {
      if (onFilesChange) {
        onFilesChange(uploadedFiles);
      }
    }, [uploadedFiles, onFilesChange]);

    // Calculate form validity as derived state
    const isFormValid = useMemo((): boolean => {
      if (loading || questions.length === 0) {
        return false;
      }
      const allQuestionsAnswered = questions.every((question) => {
        if (answers[question.Id] === undefined) {
          return false;
        }

        const selectedAnswer = getSelectedAnswer(question, answers);

        if (question.Answers && question.Answers.length > 1) {
          if (answers[question.Id] === '') {
            return false;
          }
          if (!selectedAnswer) {
            return false;
          }

          if (selectedAnswer.Condition !== undefined && selectedAnswer.Condition.trim() === '') {
            if (answers[question.Id] === '') {
              return false;
            }
          }
        }

        const tokens = tokensForQuestion(question, answers);
        const allTokensHaveValues = tokens.every((token) => {
          const value = tokenValues[question.Id]?.[token];

          if (value === undefined || value === null || value === '') {
            return false;
          }

          if (token === 'waterSupplierCompanyName') {
            const companyExists = companies.some((c) => c.Id === value);
            if (!companyExists) {
              return false;
            }
          } else {
            const placeholder = placeholders.find((p) => p.Key === token);

            // Type 2 = List
            if (placeholder && placeholder.Type === 2) {
              const validValues = placeholder.Options ?? [];
              if (validValues.length > 0 && !validValues.includes(value)) {
                return false;
              }
            }
          }

          return true;
        });

        const fileUploadRequired = isFileUploadRequired(selectedAnswer);
        const hasUploadedFile = orderItemPlans.some((url) => url.toString().includes(question.Id));
        const fileUploadValid = !fileUploadRequired || hasUploadedFile;

        return allTokensHaveValues && fileUploadValid;
      });

      return allQuestionsAnswered;
    }, [loading, questions, answers, orderItemPlans, tokenValues, companies, placeholders]);

    // Notify parent of validity changes
    useEffect(() => {
      onFormValidityChange(isFormValid);
    }, [isFormValid, onFormValidityChange]);

    // Create formatted answers as derived state
    const formattedAnswers = useMemo(() => {
      const formattedData: Record<string, SavedAnswer> = {};

      if (questions.length === 0) {
        return null;
      }

      questions.forEach((question) => {
        if (answers[question.Id] !== undefined) {
          const selectedAnswer = getSelectedAnswer(question, answers);
          if (!selectedAnswer && question.Answers?.length === 0) return;

          const answerValue = (selectedAnswer ? selectedAnswer.Condition : question.Answers?.[0]?.Condition) || DefaultOption;

          const tokens = tokensForQuestion(question, answers);
          const questionTokens = tokenValues[question.Id] || {};

          if (tokens.length > 0 && Object.keys(questionTokens).length > 0) {
            formattedData[question.Id] = {
              Value: answerValue,
              Tokens: questionTokens
            };
          } else {
            formattedData[question.Id] = {
              Value: answerValue
            };
          }
        }
      });

      return formattedData;
    }, [questions, answers, tokenValues]);

    // Expose methods to parent via ref
    useImperativeHandle(
      ref,
      () => ({
        getFormattedAnswers: () => formattedAnswers,
        clearFiles
      }),
      [formattedAnswers, clearFiles]
    );

    const triggerAutoSave = useCallback(() => {
      // Don't save if there was a load error or if there are no questions
      if (loadError || questions.length === 0) {
        return;
      }

      if (!isReadOnly) {
        void Promise.resolve(onAutoSave?.()).catch((error) => {
          console.error('Auto-save failed:', error);
        });
      }
    }, [isReadOnly, loadError, onAutoSave, questions.length]);

    const handleFileDelete = (questionId: string, fileUrl: URL) => {
      setFileToDelete({ questionId, fileUrl });
      setShowConfirmationPopup(true);
    };

    const handleBackToReason = () => {
      setShowConfirmationPopup(false);
      setFileToDelete(null);
    };

    const handleFinalConfirm = () => {
      if (fileToDelete && onFileDelete) {
        onFileDelete(fileToDelete.fileUrl);
      }
      setShowConfirmationPopup(false);
      setFileToDelete(null);
    };

    useEffect(() => {
      const fetchData = async () => {
        try {
          setLoadError(false);

          const responseList = await questionEditorService.getQuestionList({
            companyCode: 'SW',
            productType: productCode,
            isActive: true
          });
          const questionSetVersion = responseList[0]?.RowKey;

          if (!questionSetVersion) {
            setLoadError(true);
            if (onLoadError) {
              onLoadError(true);
            }
            return;
          }

          const response = await questionEditorService.getQuestions({
            partitionKey: 'SW',
            rowKey: questionSetVersion,
            downloadData: true
          });

          if (response && response.Data) {
            const typedQuestions = response.Data.Questions as Question[];
            const companiesData = response.Data.Companies || [];
            const placeholdersData = response.Data.Placeholders || [];

            setQuestions(typedQuestions);
            setPlaceholders(placeholdersData);
            setCompanies(companiesData);
            onQuestionSetVersionChange?.(questionSetVersion);
          } else {
            setLoadError(true);
            if (onLoadError) {
              onLoadError(true);
            }
          }
        } catch (error) {
          console.error('Error loading questions data:', error);
          setLoadError(true);
          if (onLoadError) {
            onLoadError(true);
          }
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    }, [onLoadError, onQuestionSetVersionChange, productCode]);

    useEffect(() => {
      let invalidTokenCount = 0;

      // Don't run validation if there was a load error or if there are no questions
      if (loadError || questions.length === 0 || Object.keys(answerValues).length === 0) {
        return;
      }

      questions.forEach((question) => {
        const savedTokens = tokenValues[question.Id] ?? {};

        Object.entries(savedTokens).forEach(([tokenKey, tokenValue]) => {
          let isValid = true;

          if (tokenKey === 'waterSupplierCompanyName' && typeof tokenValue === 'string') {
            const companyExists = companies.some((c) => c.Id === tokenValue);
            isValid = companyExists;
          } else if (typeof tokenValue === 'string') {
            // Validate placeholder list values
            const placeholder = placeholders.find((p) => p.Key === tokenKey);

            // Type 2 = List
            if (placeholder && placeholder.Type === 2) {
              const validValues = placeholder.Options ?? [];
              if (validValues.length > 0) {
                isValid = validValues.includes(tokenValue);
              }
            }
          }

          if (!isValid) {
            invalidTokenCount++;
          }
        });
      });

      if (invalidTokenCount > 0 && onInvalidTokensFound) {
        onInvalidTokensFound(true);
      }
    }, [answerValues, companies, placeholders, questions, tokenValues, onInvalidTokensFound, loadError]);

    const handleAnswerChange = (id: string, event: SelectChangeEvent) => {
      const question = questions.find((q) => q.Id === id);
      if (!question || !question.Answers) return;

      const value = event.target.value;
      let answerValue = value;

      if (value.startsWith(EmptyConditionPrefix)) {
        const index = parseInt(value.replace(EmptyConditionPrefix, ''), 10);
        answerValue = question.Answers[index]?.Condition || DefaultOption;
      } else {
        const answerIndex = findIndexOfAnswer(question, value);
        answerValue = answerIndex !== -1 ? question.Answers[answerIndex].Condition || DefaultOption : value;
      }

      onAnswerValueChange(id, answerValue);
    };

    // Handle company updates directly in the change handler
    const handleTokenValueChange = (questionId: string, token: string, value: any) => {
      onTokenValueChange(questionId, token, value);

      // Handle related company data updates immediately
      if (token === 'waterSupplierCompanyName') {
        const selectedCompany = companies.find((c) => c.Id === value);
        if (selectedCompany) {
          onTokenValueChange(questionId, 'waterSupplierAddress', selectedCompany.Address);
        }
      }
    };

    const handlePlacedOnHoldCompleted = async (comment: string) => {
      onPlacedOnHoldCompleted?.(comment);
    };

    const uploadFile = useCallback(
      async (questionId: string, file: File) => {
        if (!onFileUpload || isReadOnly) return;

        setUploadingFiles((prev) => ({ ...prev, [questionId]: true }));

        try {
          await onFileUpload(questionId, file);
          // Clear the file from state after successful upload
          setFiles((prev) => {
            const newFiles = { ...prev };
            delete newFiles[questionId];
            return newFiles;
          });
        } catch (error) {
          console.error('Failed to upload file:', error);
          // Keep the file in state if it errors, so user can retry
        } finally {
          setUploadingFiles((prev) => ({ ...prev, [questionId]: false }));
        }
      },
      [onFileUpload, isReadOnly]
    );

    const handleFileChange = useCallback(
      async (id: string, event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
          if (!isValidFileType(file)) {
            alert(import.meta.env.VITE_APP_ATTACHMENT_ALLOWED_FILE_TYPES_ALERTMSG);
            event.target.value = '';
            return;
          }

          setFiles((prev) => ({ ...prev, [id]: file }));
          await uploadFile(id, file);
          event.target.value = '';
        }
      },
      [uploadFile]
    );

    const handleDrop = useCallback(
      async (id: string, event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        const file = event.dataTransfer.files?.[0];
        if (file) {
          if (!isValidFileType(file)) {
            alert(import.meta.env.VITE_APP_ATTACHMENT_ALLOWED_FILE_TYPES_ALERTMSG);
            return;
          }

          setFiles((prev) => ({ ...prev, [id]: file }));
          await uploadFile(id, file);
        }
      },
      [uploadFile]
    );

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
    };

    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (loadError) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <Typography color="error" variant="h6">
            Error loading questions. Please refresh the page to try again.
          </Typography>
        </Box>
      );
    }

    return (
      <Stack spacing={3}>
        <DeleteAttachment open={showConfirmationPopup} onBack={handleBackToReason} onConfirm={handleFinalConfirm} />
        {questions.map((question) => {
          const tokens = tokensForQuestion(question, answers);
          const showFileUpload = shouldShowFileUpload(getSelectedAnswer(question, answers));
          const isUploading = uploadingFiles[question.Id] || false;

          return (
            <Box key={question.Id} sx={{ pb: 2 }}>
              <Typography variant="body2" gutterBottom>
                Question {question.Id}: {question.Question}
              </Typography>

              {question.Answers && question.Answers.length > 1 ? (
                <FormControl fullWidth size="small">
                  <Select
                    value={
                      answers[question.Id] !== undefined
                        ? question.Answers && /^\d+$/.test(answers[question.Id])
                          ? question.Answers[parseInt(answers[question.Id], 10)]?.Condition || ''
                          : answers[question.Id]
                        : ''
                    }
                    onChange={(event) => handleAnswerChange(question.Id, event)}
                    onBlur={triggerAutoSave}
                    displayEmpty
                    disabled={isReadOnly}
                    sx={{
                      mt: 1,
                      bgcolor: isReadOnly ? 'grey.100' : 'grey.50',
                      borderRadius: 0,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'grey.300'
                      }
                    }}
                  >
                    <MenuItem value="" disabled>
                      <em>Select an answer</em>
                    </MenuItem>
                    {question.Answers?.map((answer, index) => (
                      <MenuItem key={index} value={answer.Condition || `${EmptyConditionPrefix}${index}`}>
                        {answer.Condition || DefaultOption}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', fontStyle: 'italic' }}>
                  {question.Answers && question.Answers[0]?.Condition}
                </Typography>
              )}

              {tokens.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  {tokens.map((token) => (
                    <TokenInput
                      key={`${question.Id}-${token}`}
                      token={token}
                      questionId={question.Id}
                      tokenValues={tokenValues}
                      placeholders={placeholders}
                      companies={companies}
                      onTokenValueChange={handleTokenValueChange}
                      onPlacedOnHoldCompleted={handlePlacedOnHoldCompleted}
                      onBlur={triggerAutoSave}
                      isReadOnly={isReadOnly}
                    />
                  ))}
                </Box>
              )}

              {showFileUpload && (
                <Box sx={{ position: 'relative' }}>
                  <FileUpload
                    questionId={question.Id}
                    file={files[question.Id] || null}
                    onFileChange={handleFileChange}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    isReadOnly={isReadOnly}
                    accept={AllowedExtensions}
                    orderItemPlans={orderItemPlans}
                    onFileDelete={handleFileDelete}
                  />
                  {isUploading && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        zIndex: 1
                      }}
                    >
                      <CircularProgress size={24} />
                      <Typography variant="body2" sx={{ ml: 2 }}>
                        Uploading...
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          );
        })}

        {questions.length === 0 && (
          <Typography align="center" color="textSecondary" sx={{ py: 3 }}>
            No question data
          </Typography>
        )}
      </Stack>
    );
  }
);

Questions.displayName = 'Questions';

export default Questions;
